import api from "./apiBase";

const MODULE = "admin";

function getUserByAdmin(id) {
  return api.get(`${MODULE}/user?id=${id}`);
}

function updateDataByAdmin({ module, id, data }) {
  return api.patch(`${MODULE}?q=${module}&id=${id}`, data);
}

function getModelReports({
  page = 1,
  limit = 10,
  search = "",
  sortBy = "",
  sortOrder = "",
}) {
  const params = new URLSearchParams();
  if (page) params.append("page", page);
  if (limit) params.append("limit", limit);
  if (search) params.append("search", search);
  if (sortBy) params.append("sortBy", sortBy);
  if (sortOrder) params.append("sortOrder", sortOrder);

  return api.get(`${MODULE}/maker-reports?${params.toString()}`);
}

function calculateDesignCost(payload) {
  // payload: { filamentType: string, weightInGrams: number, printTimeInMinutes: number }
  return api.post("/designs/calculate-cost", payload);
}

function getPrintersRequested({
  page = 1,
  limit = 10,
  search = "",
  sortBy = "",
  sortOrder = "",
}) {
  const params = new URLSearchParams();
  if (page) params.append("page", page);
  if (limit) params.append("limit", limit);
  if (search) params.append("search", search);
  if (sortBy) params.append("sortBy", sortBy);
  if (sortOrder) params.append("sortOrder", sortOrder);

  return api.get(`${MODULE}?q=printersRequested&${params.toString()}`);
}

function getAdminData({
  endpoint = "",
  module,
  page = 1,
  limit = 10,
  search = "",
  filter = "",
  sortBy = "",
  sortOrder = "",
}) {
  const params = new URLSearchParams();
  if (module) params.append("q", module);
  if (page) params.append("page", page);
  if (limit) params.append("limit", limit);
  if (search) params.append("search", search);
  if (filter) params.append("filter", filter);
  if (sortBy) params.append("sortBy", sortBy);
  if (sortOrder) params.append("sortOrder", sortOrder);

  const url = endpoint
    ? `${MODULE}/${endpoint}?${params.toString()}`
    : `${MODULE}?${params.toString()}`;

  return api.get(url);
}

function changeOrderStatus(orderId, newStatus) {
  return api.patch(`${MODULE}/order/${orderId}`, { status: newStatus });
}

function confirmCommissionPayment(systemIdentifier) {
  return api.post(`${MODULE}/commissioning/${systemIdentifier}`);
}

function updateDesignHotScore(designId, hotScore) {
  return api.post(`${MODULE}/designs/${designId}/hot-score`, { hotScore });
}

function getUnfinishedOrders() {
  return api.get(`${MODULE}/open-orders`);
}

function createAdminData({ module, data, endpoint = "" }) {
  const url = endpoint
    ? `${MODULE}/${endpoint}`
    : module === "coupons"
    ? `${MODULE}/coupons`
    : `${MODULE}?q=${module}`;

  const payload = JSON.parse(JSON.stringify(data));

  return api.post(url, payload);
}

function updateCouponActive(couponId, isActive) {
  return api.patch(`${MODULE}/coupons/${couponId}/active`, { isActive });
}

function getCouponFull(couponId) {
  return api.get(`${MODULE}/coupons/${couponId}`);
}

/**
 * Gera nome/descrições via IA (OpenAI com fallback Gemini)
 */
async function generateDesignCopy(payload) {
  const res = await api.post("/admin/ai/generate-description", payload);
  return res.data;
}

function getExternalDesignProductsByDesignId(designId) {
  return api.get(`/connector-design-products/design/${designId}`);
}

function syncExternalOrdersBatch({
  page = 1,
  useExternalPriceFromIntegration = true,
  connectorId,
} = {}) {
  return api.post(
    `/external-orders-maps/nuvemshop/${connectorId}/orders/sync`,
    {
      page,
      useExternalPriceFromIntegration,
    },
  );
}

function getNuvemshopStoreCategories() {
  return api.get(`/connectors/nuvemshop/store-categories`);
}

function getStatusLogin(params = {}) {
  return api.get("/auth-refresh-tokens", { params });
}

function createDesignDraft(payload = {}) {
  const fd = new FormData();

  if (payload.modelFile) fd.append("modelFile", payload.modelFile);
  if (payload.coverImage) fd.append("coverImage", payload.coverImage);

  if (Array.isArray(payload.images)) {
    payload.images.forEach((img) => {
      if (img) fd.append("images", img);
    });
  }

  if (payload.name != null) fd.append("name", String(payload.name));
  if (payload.shortDescription != null)
    fd.append("shortDescription", String(payload.shortDescription));
  if (payload.longDescriptionHtml != null)
    fd.append("longDescriptionHtml", String(payload.longDescriptionHtml));

  if (payload.publicationStatus != null)
    fd.append("publicationStatus", String(payload.publicationStatus));

  if (payload.hotScore != null) fd.append("hotScore", String(payload.hotScore));
  if (payload.sensitiveContent != null)
    fd.append("sensitiveContent", String(!!payload.sensitiveContent));

  if (Array.isArray(payload.categories))
    fd.append("categories", JSON.stringify(payload.categories));

  if (Array.isArray(payload.tags))
    fd.append("tags", JSON.stringify(payload.tags));

  if (payload.customization)
    fd.append("customization", JSON.stringify(payload.customization));

  if (payload.connectorCategoryId !== undefined) {
    fd.append(
      "connectorCategoryId",
      payload.connectorCategoryId === null
        ? ""
        : String(payload.connectorCategoryId),
    );
  }

  if (payload.sendToExternalStore != null) {
    fd.append("sendToExternalStore", String(!!payload.sendToExternalStore));
  }

  return api.post("/designs/draft", fd);
}

function getBestSellers({
  page = 1,
  perPage = 30,
  from = "",
  to = "",
  makerId = "",
  search = "",
} = {}) {
  const params = new URLSearchParams();

  if (page) params.append("page", String(page));
  if (perPage) params.append("perPage", String(perPage));

  // opcionais (só manda se vier preenchido)
  if (from) params.append("from", String(from));
  if (to) params.append("to", String(to));

  if (makerId !== "" && makerId != null)
    params.append("makerId", String(makerId));

  if (search) params.append("search", String(search));

  return api.get(`${MODULE}/stats/best-sellers?${params.toString()}`);
}

function getDesignFull(designId) {
  if (!designId) throw new Error("designId is required");
  return api.get(`${MODULE}/designs/${designId}/full`);
}

function updateDesignFormData(designId, formData) {
  if (!designId) throw new Error("designId is required");

  return api.patch(`${MODULE}/designs/${designId}/edit`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
}

function updateDesignPublicationStatus(designId, publicationStatus) {
  if (!designId) throw new Error("designId is required");

  return api.patch(`${MODULE}/designs/${designId}/publication-status`, {
    publicationStatus,
  });
}

function getAdmin3DArchive(id) {
  return api.get(`/admin/download/${id}`);
}

function publishDesignToNuvemshop(
  designId,
  { connectorCategoryId = null } = {},
) {
  if (!designId) throw new Error("designId is required");

  return api.post(`${MODULE}/designs/${designId}/nuvemshop/publish`, {
    connectorCategoryId,
  });
}

export {
  getBestSellers,
  calculateDesignCost,
  getAdminData,
  getUserByAdmin,
  updateDataByAdmin,
  changeOrderStatus,
  confirmCommissionPayment,
  getModelReports,
  updateDesignHotScore,
  getPrintersRequested,
  getUnfinishedOrders,
  createAdminData,
  updateCouponActive,
  getCouponFull,
  generateDesignCopy,
  syncExternalOrdersBatch,
  getExternalDesignProductsByDesignId,
  getNuvemshopStoreCategories,
  createDesignDraft,
  getDesignFull,
  updateDesignFormData,
  getStatusLogin,
  updateDesignPublicationStatus,
  getAdmin3DArchive,
  publishDesignToNuvemshop,
};
